import * as React from "react";
import {View} from "react-native";
import {IconWrapperProps, IconName, ContainerName, SingleIconProps} from "./types";
import IconBase, {sizes} from "./IconBase";

export type {IconName, ContainerName, IconWrapperProps};

/**
 * Since we lazy load icons in on web, if we are unable to load an import
 * we should return a noop component so that we do not crash the page. In the future
 * we should determine a fallback icon shape with design and load that instead to not shift
 * layout, but for a quick fix for a potentially bad bug, this is okay
 *
 * @returns React.ReactNode
 */
const FallBackImport = {default: () => null};
type LazyIconComponent = React.LazyExoticComponent<React.ComponentType<SingleIconProps>>;
// A lazy component's identity must survive parent rerenders. Recreating it on hover makes
// Suspense show the fallback again, which flashes icon buttons before their hover style settles.
const lazyIconComponents = new Map<string, LazyIconComponent>();

function getLazyIconComponent(name: IconName | ContainerName): LazyIconComponent {
  const normalizedName = name.toLowerCase();
  const cachedComponent = lazyIconComponents.get(normalizedName);
  if (cachedComponent) return cachedComponent;

  const component = React.lazy(() =>
    import(`./icons/${normalizedName}.tsx`).catch(() => FallBackImport),
  );
  lazyIconComponents.set(normalizedName, component);
  return component;
}

export function Icon(props: IconWrapperProps) {
  const {
    name,
    containerName,
    color,
    iconSize,
    size,
    accessibilityLabel,
    containerColor,
    containerSize,
  } = props;
  const IconComponent = getLazyIconComponent(name);

  const ContainerComponent = containerName ? getLazyIconComponent(containerName) : undefined;

  const resolvedContainerSize = ContainerComponent ? containerSize ?? size : undefined;

  const resolvedIconSize = iconSize ?? size;
  const maxSize = resolvedContainerSize
    ? Math.max(sizes[resolvedIconSize], sizes[resolvedContainerSize])
    : sizes[resolvedIconSize];
  const FallbackElement = <View style={{width: maxSize, height: maxSize}} />;

  return (
    <React.Suspense fallback={FallbackElement}>
      {ContainerComponent && resolvedContainerSize ? (
        <IconBase
          color={color}
          containerSize={resolvedContainerSize}
          iconSize={resolvedIconSize}
          accessibilityLabel={accessibilityLabel}
          IconComponent={IconComponent}
          containerColor={containerColor}
          ContainerComponent={ContainerComponent}
        />
      ) : (
        <IconBase
          color={color}
          iconSize={resolvedIconSize}
          accessibilityLabel={accessibilityLabel}
          IconComponent={IconComponent}
        />
      )}
    </React.Suspense>
  );
}

export default React.memo(Icon);
